import { Action, Trigger } from "../types/v2/Automation";

/**
 * Trigger event types that fire at most once per card per occasion, so an
 * action that writes the card cannot re-arm its own trigger.
 *
 * Extend this set rather than editing the predicate.
 *
 * Both scheduled trigger types qualify despite setting
 * `triggerFieldChange: null`. Neither is ever evaluated by the reactive engine
 * — `shouldTrigger` has no branch for either — and each is published by its own
 * scanner, never by a card write, with a fire-once latch on the card:
 *
 * - "onPromiseTime" (WEBV3-80) latches per (automation, promised time, phase)
 *   in `promiseTimeAutomationState`.
 * - "onSchedule" (WEBV3-77) latches per (automation, card, occurrence) in
 *   `scheduledAutomationState`.
 *
 * Those latches are strictly stronger dedup than the change-based rule this
 * predicate was written around. Omitting either would make the builder refuse
 * to save its primary use case — "On a schedule -> Create a task", or a
 * promise-time toast — with a loop-safety message that does not apply.
 */
export const NON_REFIRING_EVENT_TYPES = new Set<string>([
  "onCreate",
  "onPromiseTime",
  "onSchedule",
]);

/**
 * Action fields whose application writes the card in a way that never reaches a
 * fixed point, so they must be paired with a non-refiring trigger.
 */
export const LOOP_UNSAFE_ACTION_FIELDS = new Set<string>([
  "createTask",
  "showToast",
  "resetTimer",
]);

type TriggerShape = Pick<Trigger, "triggerFieldChange" | "triggerEventType">;

/**
 * A trigger is non-refiring if it is change-based (only matches on an actual
 * field transition) or its event type fires exactly once.
 * @param {TriggerShape} t - The trigger to check.
 * @returns {boolean} True when the trigger cannot re-fire from its own write.
 */
export const isNonRefiring = (t: TriggerShape): boolean =>
  t.triggerFieldChange !== null ||
  NON_REFIRING_EVENT_TYPES.has(t.triggerEventType as string);

/**
 * Whether a trigger set is safe for a loop-unsafe action. Under AND every
 * trigger must match, so one non-refiring trigger is enough to bound firing.
 * Under OR any trigger can fire alone, so all of them must be non-refiring.
 * @param {"AND" | "OR"} conditional - How the triggers combine.
 * @param {TriggerShape[]} triggers - The automation's triggers.
 * @returns {boolean} True when the set is safe.
 */
export function isTriggerSetSafe(
  conditional: "AND" | "OR",
  triggers: TriggerShape[]
): boolean {
  if (triggers.length === 0) return false;
  return conditional === "AND"
    ? triggers.some(isNonRefiring)
    : triggers.every(isNonRefiring);
}

/**
 * Whether an action may run against a given trigger set.
 * @param {Pick<Action, "actionField">} action - The action to check.
 * @param {"AND" | "OR"} conditional - How the triggers combine.
 * @param {TriggerShape[]} triggers - The automation's triggers.
 * @returns {boolean} True when the action is safe to run.
 */
export function shouldRunAction(
  action: Pick<Action, "actionField">,
  conditional: "AND" | "OR",
  triggers: TriggerShape[]
): boolean {
  return (
    !LOOP_UNSAFE_ACTION_FIELDS.has(action.actionField as string) ||
    isTriggerSetSafe(conditional, triggers)
  );
}
